import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Filter, Sparkles, ChevronRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { API_URL } from "../lib/utils";

const fallbackImage =
  "https://images.unsplash.com/photo-1469571486292-0ba58a3f068b?w=1200&auto=format&fit=crop&q=80";

const focusAreaStyles = {
  "Humanitarian Relief": {
    badge: "bg-red-100 text-red-700 border-red-200",
    ring: "ring-red-200/60",
  },
  Education: {
    badge: "bg-blue-100 text-blue-700 border-blue-200",
    ring: "ring-blue-200/60",
  },
  "Human Rights": {
    badge: "bg-purple-100 text-purple-700 border-purple-200",
    ring: "ring-purple-200/60",
  },
  "Technology Inclusion": {
    badge: "bg-amber-100 text-amber-700 border-amber-200",
    ring: "ring-amber-200/60",
  },
  "Climate Action": {
    badge: "bg-teal-100 text-teal-700 border-teal-200",
    ring: "ring-teal-200/60",
  },
};

const BACKEND_ORIGIN = API_URL.replace(/\/api\/?$/, "");

const getImageSrc = (imageUrl) => {
  if (!imageUrl || typeof imageUrl !== "string") return fallbackImage;
  const clean = imageUrl.trim();
  if (!clean) return fallbackImage;
  if (/^https?:\/\//i.test(clean)) return clean;
  if (clean.startsWith("/")) return `${BACKEND_ORIGIN}${clean}`;
  return `${BACKEND_ORIGIN}/${clean}`;
};

export const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const [activeCategory, setActiveCategory] = useState("all");
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");

  const fetchAll = async () => {
    setLoading(true);
    setError("");
    try {
      const [projectsRes, categoriesRes] = await Promise.all([
        fetch(`${API_URL}/projects?is_active=true`),
        fetch(`${API_URL}/projects/categories`),
      ]);

      if (!projectsRes.ok) throw new Error("Failed to fetch projects");
      const projectsData = await projectsRes.json();
      const list = Array.isArray(projectsData) ? projectsData : [];
      setProjects(list);

      if (categoriesRes.ok) {
        const categoriesData = await categoriesRes.json();
        setCategories(Array.isArray(categoriesData?.categories) ? categoriesData.categories : []);
      } else {
        const unique = new Set(
          list
            .map((p) => p?.focus_area)
            .filter((v) => typeof v === "string" && v.trim())
        );
        setCategories(Array.from(unique));
      }
    } catch (err) {
      setError(err.message || "Unable to load projects");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const filteredProjects = useMemo(() => {
    let list = [...projects];

    if (activeCategory !== "all") {
      list = list.filter((p) => p?.focus_area === activeCategory);
    }

    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((p) => {
        const title = (p?.title || "").toLowerCase();
        const excerpt = (p?.excerpt || p?.description || "").toLowerCase();
        const area = (p?.focus_area || "").toLowerCase();
        return title.includes(q) || excerpt.includes(q) || area.includes(q);
      });
    }

    return list;
  }, [projects, activeCategory, search]);

  const featuredProject = useMemo(() => {
    return (
      projects[0] || {
        title: "Touch A Thousand Lives (TATL 1000)",
        excerpt:
          "Our most ambitious campaign yet—reaching vulnerable children and persons through rescue, feeding, clothing, and reintegration support.",
        image_url:
          "https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?w=1200&auto=format&fit=crop&q=80",
      }
    );
  }, [projects]);

  return (
    <div data-testid="projects-page" className="bg-white">
<section className="relative overflow-hidden py-20 md:py-28 text-white">
  {/* Background image */}
  <div
    className="absolute inset-0 bg-cover bg-center"
    style={{ backgroundImage: "url('/assets/hero-partner.png')" }}
  />

  {/* Dark overlay */}
  <div className="absolute inset-0 bg-stone-950/75" />

  {/* Optional amber glow */}
  <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(245,158,11,0.18),transparent_45%)]" />

  <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
    <div className="max-w-3xl">
      <div className="inline-flex items-center gap-2 bg-white/10 border border-white/15 rounded-full px-4 py-2 text-sm mb-5">
        <Sparkles className="w-4 h-4 text-amber-300" />
        <span className="text-white/90">Our Work in Action</span>
      </div>
      <p className="font-accent text-xl text-amber-400 mb-3">Our Work</p>
      <h1 className="font-heading text-4xl md:text-5xl font-bold mb-6 leading-tight">
        Projects Creating Measurable Community Impact
      </h1>
      <p className="text-lg text-stone-200 leading-relaxed">
        Explore the initiatives through which GLGIF transforms lives across Nigeria.
      </p>
    </div>
  </div>
</section>


      <section className="py-6 border-b border-stone-200 bg-white sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-wrap items-center gap-3 md:gap-4">
            <Filter className="w-5 h-5 text-stone-500" />
            <span className="text-stone-700 font-medium">Search Projects:</span>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by title, summary, category..."
              className="w-full sm:w-[360px] rounded-xl border border-stone-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-amber-500"
            />
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-[280px,1fr] gap-8">
          <aside className="lg:sticky lg:top-28 h-fit rounded-2xl border border-stone-200 bg-white p-4">
            <h3 className="font-semibold text-stone-900 mb-3">Project Categories</h3>
            <div className="space-y-2">
              <button
                onClick={() => setActiveCategory("all")}
                className={`w-full text-left rounded-xl px-3 py-2 text-sm transition ${
                  activeCategory === "all"
                    ? "bg-amber-100 text-amber-800 font-medium"
                    : "hover:bg-stone-100 text-stone-700"
                }`}
              >
                All Categories
              </button>

              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`w-full text-left rounded-xl px-3 py-2 text-sm transition ${
                    activeCategory === cat
                      ? "bg-amber-100 text-amber-800 font-medium"
                      : "hover:bg-stone-100 text-stone-700"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </aside>

          <div>
            {error && (
              <div className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700 text-sm">
                {error}
              </div>
            )}

            {loading ? (
              <div className="grid md:grid-cols-2 xl:grid-cols-2 gap-6">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="bg-stone-100 rounded-2xl h-[380px] animate-pulse" />
                ))}
              </div>
            ) : (
              <>
                <div className="mb-5 text-sm text-stone-500">
                  Showing <b>{filteredProjects.length}</b> project
                  {filteredProjects.length === 1 ? "" : "s"}
                  {activeCategory !== "all" ? (
                    <>
                      {" "}in <b>{activeCategory}</b>
                    </>
                  ) : null}
                </div>

                <div className="grid md:grid-cols-2 xl:grid-cols-2 gap-6">
                  {filteredProjects.map((project, index) => {
                    const style = focusAreaStyles[project.focus_area] || {
                      badge: "bg-stone-100 text-stone-700 border-stone-200",
                      ring: "ring-stone-200/60",
                    };

                    return (
                      <Card
                        key={project.id || index}
                        className={`group overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 ring-1 ${style.ring}`}
                      >
                        <div className="aspect-[16/10] relative overflow-hidden">
                          <img
                            src={getImageSrc(project.image_url)}
                            alt={project.title || "Project image"}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                            loading="lazy"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-black/10 to-transparent" />
                          <span className={`absolute top-4 left-4 text-xs md:text-sm px-3 py-1 rounded-full border ${style.badge}`}>
                            {project.focus_area || "General"}
                          </span>
                        </div>

                        <CardContent className="p-6">
                          <h3 className="font-heading text-xl font-bold text-stone-900 mb-2 line-clamp-2">
                            {project.title}
                          </h3>
                          <p className="text-stone-600 mb-5 line-clamp-3 leading-relaxed">
                            {project.excerpt || project.description}
                          </p>

                          <div className="flex flex-wrap items-center gap-3">
                            <Link
                              to="/get-involved#donate"
                              className="inline-flex items-center gap-1 text-amber-700 font-semibold hover:text-amber-800"
                            >
                              Support This Project <ArrowRight className="w-4 h-4" />
                            </Link>

                            <Link
                              to={`/projects/${project.slug || project.id}`}
                              className="inline-flex items-center gap-1 text-stone-700 font-semibold hover:text-stone-900"
                            >
                              Read More <ChevronRight className="w-4 h-4" />
                            </Link>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>

                {filteredProjects.length === 0 && (
                  <div className="text-center py-14 rounded-2xl border border-dashed border-stone-300 bg-stone-50 mt-6">
                    <p className="text-stone-600 mb-2">No projects found.</p>
                    <button
                      className="text-amber-700 font-medium hover:text-amber-800"
                      onClick={() => {
                        setActiveCategory("all");
                        setSearch("");
                      }}
                    >
                      Reset filters
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-gradient-to-r from-amber-600 to-amber-700 text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <span className="inline-block bg-white/20 px-4 py-2 rounded-full text-sm font-medium mb-4">
              Featured Campaign
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
              {featuredProject.title || "Touch A Thousand Lives (TATL 1000)"}
            </h2>
            <p className="text-lg text-white/90 mb-8 leading-relaxed">
              {featuredProject.excerpt || featuredProject.description}
            </p>
            <Button asChild className="bg-white text-amber-700 hover:bg-white/90 rounded-full px-8 py-4">
              <Link to="/events">View Campaign Events <ArrowRight className="ml-2 w-4 h-4" /></Link>
            </Button>
          </div>
          <div>
            <img
              src={getImageSrc(featuredProject.image_url)}
              alt={featuredProject.title || "Featured Campaign"}
              className="rounded-2xl shadow-2xl border border-white/20"
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default Projects;
